<template>
  <component :is="tag" class="omx-skel" :class="['omx-skel--' + variant, rounded ? 'omx-skel--rounded' : '']" :style="dim" aria-hidden="true">
    <template v-if="variant === 'text'">
      <span class="omx-skel__line" v-for="i in lines" :key="i" :style="{ width: lineWidth(i) }"></span>
    </template>
    <template v-else-if="variant === 'table-row'">
      <span class="omx-skel__cell" v-for="i in columns" :key="i" :style="{ flex: cellFlex(i) }"></span>
    </template>
    <template v-else-if="variant === 'card'">
      <span class="omx-skel__line" style="width:55%;height:14px"></span>
      <span class="omx-skel__line" style="width:80%"></span>
      <span class="omx-skel__line" style="width:70%"></span>
      <span class="omx-skel__line" style="width:40%"></span>
    </template>
  </component>
</template>

<script>
/**
 * OmxSkeleton — per-section loading placeholder.
 *
 * Replaces the heavy global <b-loading> takeover with a localised shimmer
 * that preserves layout. Used inside lists, cards, and detail panes while
 * data is fetched.
 *
 * Examples:
 *   <OmxSkeleton variant="text" :lines="3" />
 *   <OmxSkeleton variant="table-row" :columns="[2, 1, 1, 1, 0.6]" />   <!-- flex ratios -->
 *   <OmxSkeleton variant="block" height="120px" />
 *   <OmxSkeleton variant="card" />
 *
 * Props:
 *   variant: 'text' | 'block' | 'table-row' | 'card' (default 'block')
 *   lines: number of text lines (only for 'text')
 *   columns: number | number[] — count, or array of flex ratios (for 'table-row')
 *   width / height: any CSS length, used for 'block'
 *   rounded: round corners (default true)
 *   tag: wrapper tag (default 'div')
 */
export default {
  name: "OmxSkeleton",
  props: {
    variant: { type: String, default: "block" },
    lines: { type: Number, default: 3 },
    columns: { type: [Number, Array], default: 5 },
    width: { type: String, default: "100%" },
    height: { type: String, default: "14px" },
    rounded: { type: Boolean, default: true },
    tag: { type: String, default: "div" }
  },
  computed: {
    dim() {
      if (this.variant === "block") {
        return { width: this.width, height: this.height };
      }
      return {};
    }
  },
  methods: {
    lineWidth(i) {
      // last line shorter — natural reading rhythm
      if (i === this.lines) return "55%";
      const widths = ["92%", "84%", "78%", "88%"];
      return widths[(i - 1) % widths.length];
    },
    cellFlex(i) {
      if (Array.isArray(this.columns)) {
        return this.columns[i - 1] || 1;
      }
      return 1;
    },
    columnCount() {
      return Array.isArray(this.columns) ? this.columns.length : this.columns;
    }
  },
  beforeMount() {
    if (this.variant === "table-row" && typeof this.columns === "number") {
      // normalise so the v-for iterates the right amount
      this._normalized = this.columns;
    }
  }
};
</script>

<style scoped lang="scss">
.omx-skel {
  display: block;
  &--rounded { border-radius: var(--omx-radius-md); }
}

.omx-skel--text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.omx-skel--block {
  background: linear-gradient(90deg, var(--omx-ink-100) 0%, var(--omx-ink-50) 40%, var(--omx-ink-100) 80%);
  background-size: 480px 100%;
  animation: omx-shimmer 1.4s linear infinite;
}

.omx-skel--table-row {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--omx-ink-100);
}

.omx-skel--card {
  background: var(--omx-surface);
  border: 1px solid var(--omx-ink-100);
  border-radius: var(--omx-radius-lg);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.omx-skel__line,
.omx-skel__cell {
  display: block;
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, var(--omx-ink-100) 0%, var(--omx-ink-50) 40%, var(--omx-ink-100) 80%);
  background-size: 480px 100%;
  animation: omx-shimmer 1.4s linear infinite;
}

.omx-skel__cell { height: 14px; }

@media (prefers-reduced-motion: reduce) {
  .omx-skel--block,
  .omx-skel__line,
  .omx-skel__cell {
    animation: none;
  }
}
</style>
